import { useRef, useState } from 'react';
import type { KeyboardEvent, ReactNode } from 'react';
import { EllipsisHorizontalIcon } from '@heroicons/react/24/outline';
import { AnchoredPanel } from './AnchoredPanel';
import { Button } from './Button';

// ------------------------------------------------------------------
// OverflowMenu — story 2-137 (P10 §2, control/action grammar).
// ------------------------------------------------------------------
//
// THE GAP. "A region normally has ONE primary action; secondary actions stay
// quiet; infrequent actions collapse into an overflow menu rather than
// wrapping" (this story's own done-when) had no primitive at HEAD — every
// action row that outgrew a couple of buttons either wrapped onto a second
// line (RailPanel headers, PageHeader toolbars) or grew its own bespoke
// dropdown. There is no second positioning mechanism here: this is
// `AnchoredPanel` (the existing anchored-popover primitive — portal,
// viewport-flip, scroll-track, Esc + backdrop close, focus return) with a
// `role="menu"` panel and real `role="menuitem"` rows, not a new popover
// engine. `AnchoredPanel` grew one new prop for this (`role`, defaulting to
// `dialog` — every pre-existing consumer is unaffected) rather than this
// file reimplementing viewport-flip/scroll-tracking/focus-return by hand.
//
// ── a11y (WAI-ARIA menu button pattern) ────────────────────────────────────
//  * Trigger: an icon-only `Button` (`aria-haspopup="menu"`, `aria-expanded`)
//    — Tab reaches it, Enter/Space/click opens the menu.
//  * Panel: `role="menu"` (via `AnchoredPanel`'s new `role` prop), named by
//    `label`. Esc and backdrop-click close it (AnchoredPanel's existing
//    mechanics); focus returns to the trigger on close either way.
//  * Items: real `<button role="menuitem">` — activated by click OR
//    Enter/Space, the native button behaviour, not reimplemented.
//  * ROVING TABINDEX + arrow keys: ArrowDown/ArrowUp move focus between
//    enabled items and wrap; Home/End jump to the ends — same shape `Tabs`
//    already established for its tablist, applied to a menu instead of a
//    tab strip. `AnchoredPanel` auto-focuses the first focusable element on
//    open (its existing `initialFocus` behaviour), which lands on the first
//    item regardless of this file's own tabIndex bookkeeping — the roving
//    index only matters for what stays reachable if focus moves and returns.
//  * Tab: closes the menu rather than trapping focus inside it —
//    `AnchoredPanel` never traps Tab (unlike `Modal`), so leaving it open
//    while focus moves elsewhere in the page would be a visible stale
//    popover; closing on Tab keeps the "one thing open at a time" contract
//    every other AnchoredPanel consumer already gets for free from Esc/
//    backdrop-close.
//  * Disabled actions stay in the list (never hidden) with `disabled`, so a
//    person can see WHAT exists even when they can't use it right now.

export interface OverflowMenuAction {
  /** Stable identity — also what roving focus and the demo tests key on. */
  key: string;
  label: string;
  /** Decorative — the label carries the meaning. */
  icon?: ReactNode;
  onSelect: () => void;
  disabled?: boolean;
  /** Renders the item in `--destructive` (e.g. "Delete", "Revoke"). */
  destructive?: boolean;
}

export interface OverflowMenuProps {
  actions: OverflowMenuAction[];
  /** Accessible name for both the trigger button and the menu region.
   * @default 'More actions' */
  label?: string;
  align?: 'left' | 'right';
  /** Matches `Button`'s own size scale — the trigger is a DS `Button`. */
  size?: 'sm' | 'md';
  className?: string;
  'data-testid'?: string;
}

export function OverflowMenu({
  actions,
  label = 'More actions',
  align = 'right',
  size = 'md',
  className = '',
  'data-testid': testId,
}: OverflowMenuProps): JSX.Element {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const itemRefs = useRef<Record<string, HTMLButtonElement | null>>({});

  const enabled = actions.filter((a) => !a.disabled);

  const focusByKey = (key: string | undefined) => {
    if (key) itemRefs.current[key]?.focus();
  };

  const move = (fromKey: string | undefined, delta: number) => {
    if (enabled.length === 0) return;
    const current = enabled.findIndex((a) => a.key === fromKey);
    const next = enabled[(current + delta + enabled.length) % enabled.length];
    focusByKey(next?.key);
  };

  const onMenuKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const activeKey = actions.find((a) => itemRefs.current[a.key] === document.activeElement)?.key;
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        move(activeKey, 1);
        break;
      case 'ArrowUp':
        event.preventDefault();
        move(activeKey, -1);
        break;
      case 'Home':
        event.preventDefault();
        focusByKey(enabled[0]?.key);
        break;
      case 'End':
        event.preventDefault();
        focusByKey(enabled[enabled.length - 1]?.key);
        break;
      case 'Tab':
        // Never trap — see the header note. Let the browser move focus and
        // close so nothing stale is left open.
        setOpen(false);
        break;
      default:
        break;
    }
  };

  return (
    <>
      <Button
        ref={triggerRef}
        type="button"
        variant="ghost"
        size={size}
        icon={<EllipsisHorizontalIcon className="h-4 w-4" />}
        iconOnly
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        className={className}
        data-testid={testId}
      >
        {label}
      </Button>
      {open && triggerRef.current && (
        <AnchoredPanel
          anchor={triggerRef.current}
          align={align}
          width={224}
          role="menu"
          label={label}
          onClose={() => setOpen(false)}
        >
          {/* `role="none"` keeps this wrapper invisible to the a11y tree so the
              menu's direct semantic children read as menuitems, while still
              giving the roving-tabindex keydown handler a place to live. */}
          <div role="none" className="py-1" onKeyDown={onMenuKeyDown}>
            {actions.map((action, index) => (
              <button
                key={action.key}
                type="button"
                role="menuitem"
                ref={(node) => {
                  itemRefs.current[action.key] = node;
                }}
                tabIndex={index === 0 ? 0 : -1}
                disabled={action.disabled}
                onClick={() => {
                  action.onSelect();
                  setOpen(false);
                }}
                className={`focus-ring flex w-full min-w-0 items-center gap-2 px-3 py-2 text-left text-sm hover:bg-muted disabled:pointer-events-none disabled:opacity-50 ${
                  action.destructive ? 'text-destructive' : 'text-foreground'
                }`}
              >
                {action.icon && (
                  <span aria-hidden="true" className="flex shrink-0 items-center">
                    {action.icon}
                  </span>
                )}
                <span className="min-w-0 flex-1 truncate">{action.label}</span>
              </button>
            ))}
          </div>
        </AnchoredPanel>
      )}
    </>
  );
}
